
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";
import { Conversation } from "@/types/conversation";
import { MessageTone, Message } from "@/types/message";
import MessageFormatToggle from "@/components/refund/basic-info/MessageFormatToggle";
import RegenerateButton from "@/components/refund/messages/RegenerateButton";
import { debugEnvironment } from "@/utils/environment-debug";

interface MessageFormatSectionProps {
  conversation: Conversation;
  onFormatUpdate?: (format: "email" | "chat") => void;
  onRegenerateLatestAI?: (messageId: string, tone: MessageTone, customInstructions?: string) => Promise<Message | null>;
  latestAIMessageId?: string;
  isRegenerating?: boolean;
}

const MessageFormatSection = ({ 
  conversation, 
  onFormatUpdate, 
  onRegenerateLatestAI, 
  latestAIMessageId, 
  isRegenerating = false 
}: MessageFormatSectionProps) => {
  const { toast } = useToast();
  const [messageFormat, setMessageFormat] = useState<"email" | "chat">("email");
  const [isUpdating, setIsUpdating] = useState(false);
  
  const handleRegenerateWithNewFormat = async () => {
    if (onRegenerateLatestAI && latestAIMessageId) {
      await onRegenerateLatestAI(latestAIMessageId, "polite");
    }
  };

  // Environment debugging
  useEffect(() => {
    const envInfo = debugEnvironment();
    console.log(`🔧 MessageFormatSection Environment: ${envInfo.environment}`);
  }, []);

  // Sync with conversation data ONLY on mount or when conversation ID changes
  useEffect(() => {
    if (conversation?.message_format) {
      console.log("🔄 MessageFormatSection: INITIAL sync format from conversation:", conversation.message_format);
      console.log("🌍 Environment context:", debugEnvironment().environment);
      
      // Runtime validation to ensure format is valid
      const validFormat = conversation.message_format === "chat" ? "chat" : "email";
      if (validFormat !== conversation.message_format) {
        console.warn("⚠️ Invalid message format detected, defaulting to email:", conversation.message_format);
      }
      
      console.log(`✅ Setting messageFormat to: ${validFormat} in ${debugEnvironment().environment}`);
      setMessageFormat(validFormat);
    }
  }, [conversation?.id]); // Only sync when conversation ID changes, not on every prop change

  const handleMessageFormatChange = async (format: "email" | "chat") => {
    const envInfo = debugEnvironment();
    
    if (!conversation) {
      console.error("❌ No conversation data available");
      toast({
        variant: "destructive",
        title: "Error",
        description: "No conversation data available",
      });
      return;
    }
    
    console.log(`🔄 MessageFormatSection: USER INITIATED format change to: ${format} in ${envInfo.environment}`);
    console.log("🔍 Current conversation format before change:", conversation.message_format);
    console.log("🌍 Production environment debug:", {
      hostname: window.location.hostname,
      isProduction: envInfo.isProduction,
      userAgent: navigator.userAgent.substring(0, 50) + '...'
    });
    
    setIsUpdating(true);
    
    // Store the old format for potential reversion
    const oldFormat = messageFormat;
    
    try {
      // 1. Update local state immediately
      console.log(`🔄 MessageFormatSection: Setting local state to: ${format} in ${envInfo.environment}`);
      setMessageFormat(format);
      
      // 2. Notify parent component immediately to prevent overwrites
      if (onFormatUpdate) {
        console.log(`🔄 MessageFormatSection: Notifying parent of format change in ${envInfo.environment}`);
        onFormatUpdate(format);
      }
      
      // 3. Update database with atomic operation
      console.log(`🔄 MessageFormatSection: Updating database for conversation: ${conversation.id} in ${envInfo.environment}`);
      
      // Add environment-specific headers for debugging
      const updatePayload = { 
        message_format: format,
        updated_at: new Date().toISOString()
      };
      
      console.log(`📤 Database update payload in ${envInfo.environment}:`, updatePayload);
      
      const { data: updatedData, error: updateError } = await supabase
        .from("refund_requests")
        .update(updatePayload)
        .eq("id", conversation.id)
        .select("message_format")
        .single();
        
      if (updateError) {
        console.error(`❌ Database update error in ${envInfo.environment}:`, updateError);
        // Revert all changes on error
        setMessageFormat(oldFormat);
        if (onFormatUpdate) {
          onFormatUpdate(oldFormat);
        }
        toast({
          variant: "destructive",
          title: "Update Failed",
          description: `Failed to update message format: ${updateError.message}`,
        });
        return;
      }
      
      console.log(`✅ Database update successful for format: ${format} in ${envInfo.environment}`);
      console.log(`✅ Database returned format: ${updatedData?.message_format} in ${envInfo.environment}`);
      
      // Validate the database actually updated correctly
      if (updatedData?.message_format !== format) {
        console.error(`❌ Database update mismatch in ${envInfo.environment}! Expected: ${format}, Got: ${updatedData?.message_format}`);
        throw new Error("Database update validation failed");
      }
      
      // Environment-specific success message
      const successMessage = envInfo.isProduction 
        ? `Message format updated to ${format} (Production)`
        : `Message format changed to ${format}. New AI responses will use this format.`;
      
      toast({
        title: "Format Updated",
        description: successMessage,
      });
      
    } catch (error: any) {
      console.error(`❌ Unexpected error updating message format in ${envInfo.environment}:`, error);
      // Revert all changes on error
      setMessageFormat(oldFormat);
      if (onFormatUpdate) {
        onFormatUpdate(oldFormat);
      }
      toast({
        variant: "destructive",
        title: "Unexpected Error",
        description: `Failed to update message format: ${error.message}`,
      });
    } finally {
      setIsUpdating(false);
    }
  };

  // Add debugging to track any unwanted format changes
  useEffect(() => {
    const envInfo = debugEnvironment();
    console.log(`🔍 MessageFormatSection: Local messageFormat state changed to: ${messageFormat} in ${envInfo.environment}`);
  }, [messageFormat]);

  // Add debugging to track conversation prop changes
  useEffect(() => {
    const envInfo = debugEnvironment();
    console.log(`🔍 MessageFormatSection: Conversation prop changed in ${envInfo.environment}:`, {
      id: conversation?.id,
      message_format: conversation?.message_format
    });
  }, [conversation]);

  return (
    <div className="flex justify-between items-center mb-10">
      <div className="flex items-center gap-4">
        <h2 className="text-xl font-semibold">Message Format</h2>
        <div className={isUpdating ? "opacity-50 pointer-events-none" : ""}>
          <MessageFormatToggle 
            messageFormat={messageFormat}
            onFormatChange={handleMessageFormatChange}
          />
        </div>
        {onRegenerateLatestAI && latestAIMessageId && (
          <RegenerateButton
            onClick={handleRegenerateWithNewFormat}
            isRegenerating={isRegenerating}
            isInitialMessage={false}
          />
        )}
        {isUpdating && (
          <span className="text-sm text-muted-foreground">Updating...</span>
        )}
      </div>
    </div>
  );
};

export default MessageFormatSection;
